import { Button } from "@progress/kendo-react-buttons";
import { Card, GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import {
  Field,
  FieldArrayRenderProps,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setJadaiEntryItemIndex } from "./jadaiSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import { deleteJadaiEntryItems } from "./services/jadai.services";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import { getItemById } from "../item/services/item.services";
import FormDatePicker from "../../components/formFields/FormDateField";

interface ChangeTotalPhineWatchers {
  formRenderProps: FormRenderProps;
  index: number;
}

const ChangeTotalPhineWatcher: React.FC<ChangeTotalPhineWatchers> = ({
  formRenderProps,
  index,
}) => {
  const dispatch = useAppDispatch();
  const given_weight =
    +formRenderProps.valueGetter(`jadai_Entry_item.${index}.given_weight`) || 1;
  const given_purity =
    +formRenderProps.valueGetter(`jadai_Entry_item.${index}.given_purity`) || 0;
  const given_wastage =
    +formRenderProps.valueGetter(`jadai_Entry_item.${index}.given_wastage`) ||
    0;
  const given_wastage_type = formRenderProps.valueGetter(
    `jadai_Entry_item.${index}.given_wastage_type`,
  );

  const item_id = formRenderProps.valueGetter(
    `jadai_Entry_item.${index}.item_id`,
  );
  const isFirstRun = useRef(true);
  const PurityList = useAppSelector((state) => state.purity.PurityList);
  const ItemList = useAppSelector((state) => state.item.ItemList);

  const fetchItem = async (id: any) => {
    const res: any = await dispatch(getItemById(id));
    if (res.meta.requestStatus === "fulfilled") {
      const purity_id = res.payload.purity_id;
      if (purity_id && PurityList && PurityList?.length > 0) {
        const purity = PurityList.find(
          (purity: any) => purity.ID === purity_id,
        )?.purity;

        if (purity) {
          formRenderProps.onChange(`jadai_Entry_item.${index}.given_purity`, {
            value: purity,
          });
        } else {
          formRenderProps.onChange(`jadai_Entry_item.${index}.given_purity`, {
            value: 0,
          });
        }
      } else {
        formRenderProps.onChange(`jadai_Entry_item.${index}.given_purity`, {
          value: 0,
        });
      }
    }
  };

  useEffect(() => {
    let phine = 0;
    if (given_wastage_type === 1) {
      phine = given_weight * (given_purity / 100 + given_wastage);
    } else {
      phine = given_weight * ((given_purity + given_wastage) / 100);
    }
    if (given_weight && given_purity) {
      formRenderProps.onChange(`jadai_Entry_item.${index}.phine`, {
        value: phine.toFixed(3),
      });
    }
  }, [given_weight, given_purity, given_wastage]);

  useEffect(() => {
    if (isFirstRun.current) {
      // ⛔ skip the first run on mount (reload)
      isFirstRun.current = false;
      return;
    }
    if (ItemList && ItemList?.length > 0 && item_id) {
      const ItemGUID = ItemList.find(
        (item: any) => item.id === item_id,
      )?.item_guid;
      if (ItemGUID) {
        fetchItem(ItemGUID);
      }
    }
  }, [item_id]);

  return null;
};

const JadaiEntryDetailArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const JadaiEntryItemIndex = useAppSelector(
    (state) => state.jadai.JadaiEntryItemIndex,
  );
  const name = fieldArrayRenderProps?.name;
  const karigar_given_wastage =
    +fieldArrayRenderProps.formRenderProps.valueGetter(
      `karigar_given_wastage`,
    ) || 0;
  const karigar_given_wastage_type =
    fieldArrayRenderProps.formRenderProps.valueGetter(
      `karigar_given_wastage_type`,
    );
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = React.useCallback(
    async (index: number) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        const response = await dispatch(
          deleteJadaiEntryItems(fieldArrayRenderProps?.value[index].id),
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeDialog());
          dispatch(setJadaiEntryItemIndex(-1));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeDialog());
        dispatch(setJadaiEntryItemIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );
  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        IssueDate: new Date(),
        item_id: null,
        given_weight: null,
        given_purity: null,
        given_wastage: karigar_given_wastage,
        given_wastage_type: karigar_given_wastage_type,
        phine: null,
        pcs: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openDialog("JadaiEntryDeleteDialog"));
    dispatch(setJadaiEntryItemIndex(index));
  };

  return (
    <GridLayout cols={[{ width: "100%" }]}>
      {dialogName === "JadaiEntryDeleteDialog" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Jadai Entry Data"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(JadaiEntryItemIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value.map(
          (jadai_Entry_item: any, index: number) => (
            <GridLayoutItem key={index}>
              <ChangeTotalPhineWatcher
                formRenderProps={fieldArrayRenderProps.formRenderProps}
                index={index}
              />
              <Card className="fieldarray-card">
                <GridLayout
                  style={{ marginRight: 80 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "12%" },
                    { width: "15%" },
                    { width: "15%" },
                    { width: "15%" },
                    { width: "15%" },
                    { width: "15%" },
                    { width: "10%" },
                    { width: "5%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      label="Issue Date"
                      name={`${name}.${index}.IssueDate`}
                      component={FormDatePicker}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.item_id`}
                      label="Item"
                      placeholder="Select Item"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={ItemList?.map((item: IItem) => {
                        return {
                          value: item.id,
                          label: item.item_name,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.given_weight`}
                      label="Given Weight  (gm)"
                      placeholder="0.00"
                      type="number"
                      format="n3"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.given_purity`}
                      label="Given purity"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.given_wastage`}
                      label="Given Wastage"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.phine`}
                      label="Phine (gm)"
                      placeholder="Phine"
                      type="number"
                      disabled
                      defaultValue={0}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`${name}.${index}.pcs`}
                      label="PCS"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      marginTop: 42,
                    }}
                  >
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(index)}
                      // disabled={fieldArrayRenderProps.value.length === 1}
                      themeColor={"error"}
                      size={"small"}
                      fillMode={"solid"}
                      type="button"
                      title="Delete"
                      style={{ height: 28, width: 28 }}
                    >
                      <MdDelete
                        className="absolute-position"
                        style={{ fontSize: "16px" }}
                      />
                    </IconButton>
                  </GridLayoutItem>
                </GridLayout>
              </Card>
            </GridLayoutItem>
          ),
        )}
      <GridLayoutItem style={{ marginBottom: 10, textAlign: "left" }}>
        <Button
          fillMode={"solid"}
          themeColor={"primary"}
          onClick={pushElementInarray}
          type="button"
        >
          Add New
        </Button>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default JadaiEntryDetailArray;
